import { client } from './client';
import { OrganizationInfo, SpaceInfo, StatusResponse } from './types';

export interface CreateSpaceParams {
  title: string;
  organizationIds: number[];
  placeIds: number[];
  administrator: string;
}

export const createSpace = async (
  params: CreateSpaceParams,
): Promise<SpaceInfo> => {
  try {
    const response = await client.post<SpaceInfo>('/api/v2/spaces', params);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const readSpace = async (spaceId: number): Promise<SpaceInfo> => {
  try {
    const response = await client.get<SpaceInfo>(`/api/v2/spaces/${spaceId}`);
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface FindSpacesParams {
  searchText: string | undefined;
  pageSize: number;
  pageNumber: number;
}

export interface FindSpacesResponse {
  totalRows: number;
  pageNumber: number;
  pageSize: number;
  pageItems: SpaceInfo[];
}

export const findSpaces = async (
  params: FindSpacesParams,
): Promise<SpaceInfo[]> => {
  try {
    const response = await client.get<FindSpacesResponse>('/api/v2/spaces', {
      params,
    });
    return response.data.pageItems;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface UpdateSpaceParams {
  spaceId: number;
  title: string;
  organizationIds: number[];
  placeIds: number[];
  administrator: string;
}

export const updateSpace = async (params: UpdateSpaceParams): Promise<void> => {
  try {
    await client.put<StatusResponse>('/api/v2/spaces', params);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const deleteSpace = async (spaceId: number): Promise<void> => {
  try {
    await client.delete<StatusResponse>(`/api/v2/spaces/${spaceId}`);
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface FindOrganizationsParams {
  searchText: string;
  itemOffset: number;
  itemLimit: number;
  employeeNumber?: string | undefined;
}

export interface FindOrganizationsResponse {
  organizations: OrganizationInfo[];
}

export const findOrganizations = async (
  params: FindOrganizationsParams,
): Promise<OrganizationInfo[]> => {
  try {
    const response = await client.get<FindOrganizationsResponse>(
      '/api/v2/organizations',
      { params },
    );
    return response.data.organizations;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const getOrganizationInfo = async (
  organizationId: number,
): Promise<OrganizationInfo> => {
  try {
    const response = await client.get<OrganizationInfo>(
      `/api/v2/organizations/${organizationId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export interface PlaceSpace {
  placeId: number;
  spaceId: number;
}

export interface CheckPlacesResponse {
  usedPlaces: PlaceSpace[];
}

export const checkPlaces = async (ids: number[]): Promise<PlaceSpace[]> => {
  try {
    const query = ids.map(s => `ids=${s}`).join('&');
    const response = await client.get<CheckPlacesResponse>(
      `/api/v2/spaces/checkPlaces?${query}`,
    );
    return response.data.usedPlaces;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};

export const getEmployeeSpaces = async (id: number): Promise<number[]> => {
  try {
    const response = await client.get<number[]>(
      `/api/v2/employees/${id}/spaces`,
    );
    return response.data;
  } catch (error) {
    console.error('Unable to call server.', error);
    throw error;
  }
};
